/* @flow */
/**
 * Manages geo email group table data, filters, dropdown values, and related API actions.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { getFilterDropdownValuesFromResponse, getFilteredResults, sortDataAscendingConfig } from '../../helpers/dataHelpers';

const name = 'geoEmailGroup';

function createInitialState() {
  return {
    dataId: '',
    dataRows: [],
    settingsFilterId: '',
    selectedFilters: {
      emailID: [],
      geoEmailList: []
    },
    filterDropdownValues: {
    },
    geoEmailConfigScreenData: null,
    unfilteredConfigScreenData: null
  };
}

export const initialState = createInitialState();

/**
 * Stores geo email group rows and refreshes the table data id.
 */
function setGeoEmailGroups(state: Object, action: Object) {
  const dataId = cryptoRand().toString().substr(2, 8);
  const dataRows = action.payload.objects;
  return {
    ...state,
    dataRows,
    dataId
  };
}

/**
 * Updates geo email table data while preserving unfiltered rows for filter resets.
 */
function updateGeoEmailScreenData(state: Object, action: Object) {
  const payload = {
    data: action.payload.flattenedResponse,
    // Use filtered results when present, otherwise display all fetched rows.
    filteredResults: action.payload.filteredResults || action.payload.flattenedResponse
  };

  return {
    ...state,
    geoEmailConfigScreenData: payload.filteredResults,
    unfilteredConfigScreenData: payload.data,
    dataId: cryptoRand().toString().substr(2, 8)
  };
}

/**
 * Clears selected geo email filters and restores the unfiltered table data.
 */
function clearGeoEmailFilters(state: Object) {
  return {
    ...state,
    selectedFilters: {
      ...initialState.selectedFilters
    },
    geoEmailConfigScreenData: state.unfilteredConfigScreenData
  };
}

/**
 * Replaces the visible geo email table rows after filter application.
 */
function updateFilteredGeoEmailConfigScreen(state: Object, action: Object) {
  const payload = {
    data: action.payload
  };

  return {
    ...state,
    geoEmailConfigScreenData: payload.data,
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

/**
 * Updates one selected filter list in geo email state.
 */
function updateFilterValues(state: Object, action: Object) {
  if (Array.isArray(action.payload.values) && action.payload.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        // Dynamic filter names allow the same reducer to update any configured filter.
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}

// SETTINGS_UPDATE_FILTER_DROPDOWN_VALUES
/**
 * Stores sorted dropdown filter values derived from geo email results.
 */
function updateFilterDropdownValues(state: Object, action: Object) {
  if (
    action.payload && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: sortDataAscendingConfig(action.payload)
    };
  }
  return { ...state };
}

// SET_SETTINGS_FILTER_ID
/**
 * Refreshes the geo email filter request id to notify filter-dependent UI.
 */
function updateGeoEmailRequestFilterId(state: Object) {
  return { ...state, settingsFilterId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Collects geo email reducers for slice creation.
 */
function createReducers() {
  return {
    setGeoEmailGroups,
    updateGeoEmailScreenData,
    clearGeoEmailFilters,
    updateFilteredGeoEmailConfigScreen,
    updateFilterValues,
    updateFilterDropdownValues,
    updateGeoEmailRequestFilterId
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Fetches geo email group data for a specific email id from getGeoEmailGroup.
 */
function getGeoEmailGroupDataByEmailID() {
  return createAsyncThunk(
    `${name}/getGeoEmailGroupDataByEmailID`,
    async (
      {
        email,
        callback
      },
      { dispatch }
    ) => {
      dispatch(toggleSpinner(true));
      // Backend filtering is scoped to the requested email id.
      const query = `?filter=emailID(${email})`;
      request(
        {
          api: 'getGeoEmailGroup',
          method: 'get',
          routeParams: { query }
        },
        dispatch
      )
        .then((response) => {
          // Flatten nested API data before returning it through the callback.
          const flattenedResponse = getflattenResponse(response.data);
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(flattenedResponse);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          console.log(error);
          callback(error);
        });
    }
  );
}

/**
 * Fetches active geo email groups and updates table rows plus filter dropdown values.
 */
function getGeoEmailGroupData() {
  return createAsyncThunk(
    `${name}/getGeoEmailGroupData`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      dispatch(toggleSpinner(true));
      // Only active geo email groups are shown on the configuration screen.
      const query = '?filter=isActive(true)';
      request(
        {
          api: 'getGeoEmailGroup',
          method: 'get',
          routeParams: { query }
        },
        dispatch
      )
        .then((response) => {
          const state = getState();
          const flattenedResponse = getflattenResponse(response.data);
          const { selectedFilters } = state.geoEmail;
          // Reapply existing filters after fresh data is loaded.
          const filteredResults = getFilteredResults(flattenedResponse.objects, selectedFilters, 'geoEmailGroup');
          dispatch(actions.updateGeoEmailScreenData(
            { flattenedResponse: flattenedResponse.objects, filteredResults }
          ));
          dispatch(actions.updateFilterDropdownValues(
            // Dropdown options are generated from the full unfiltered result set.
            getFilterDropdownValuesFromResponse(flattenedResponse.objects, [
              'emailID',
              'geoEmailList'
            ], 'geoEmailGroup')
          ));
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          console.log(error);
          callback(error);
        });
    }
  );
}

/**
 * Adds a new geo email group by converting editable rows into the API request body.
 */
function addNewGeoGroupEmail() {
  return createAsyncThunk(
    `${name}/addNewGeoGroupEmail`,
    async (
      {
        data,
        callback
      },
      { dispatch }
    ) => {
      let groupEmail;
      dispatch(toggleSpinner(true));
      const geoMembersData = [];
      data.forEach((row, index) => {
        if (index === 0) {
          // The first row carries the parent group email id.
          groupEmail = row.emailID;
        }
        if (row.geoEmailList && row.name) {
          // Member rows become the geoEmailList payload expected by the API.
          geoMembersData.push({
            emailID: row.geoEmailList,
            name: row.name
          });
        }
      });
      const geoEmailRequestBody = {
        emailID: groupEmail,
        geoEmailList: geoMembersData
      };
      request(
        {
          api: 'geoEmailGroup',
          method: 'post',
          data: geoEmailRequestBody,
          cancellable: true
        },
        dispatch
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(error);
        });
    }
  );
}

/**
 * Sends edited geo email group rows to the geoEmailGroup update API.
 */
function updateGeoEmailGroupData() {
  return createAsyncThunk(
    `${name}/updateGeoEmailGroupData`,
    async (
      {
        data,
        callback
      },
      { dispatch }
    ) => {
      const updateRequestBody = {
        emailGroupListData: data
      };
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'geoEmailGroup',
          method: 'put',
          data: updateRequestBody,
          cancellable: true
        },
        dispatch
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(error);
        });
    }
  );
}

/**
 * Applies geo email filters in Redux state and refreshes visible table rows.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (
      { payload },
      { getState, dispatch }
    ) => {
      try {
        dispatch(actions.updateFilterValues(payload));
        const state = getState();
        const { selectedFilters, unfilteredConfigScreenData } = state.geoEmail;
        // Filtering always starts from the cached unfiltered data set.
        const filteredResults = getFilteredResults(unfilteredConfigScreenData, selectedFilters, 'geoEmailGroup');
        dispatch(actions.updateFilteredGeoEmailConfigScreen(filteredResults));
      } catch (error) {
        console.log(error);
      }
    }
  );
}

/**
 * Clears geo email filters and restores the unfiltered result set.
 */
function clearFilters() {
  return createAsyncThunk(
    `${name}/clearFilters`,
    async (_, { dispatch }) => {
      dispatch(actions.clearGeoEmailFilters());
    }
  );
}

/**
 * Creates geo email async thunks exposed alongside slice actions.
 */
function createExtraActions() {
  return {
    getGeoEmailGroupDataByEmailID: getGeoEmailGroupDataByEmailID(),
    getGeoEmailGroupData: getGeoEmailGroupData(),
    addNewGeoGroupEmail: addNewGeoGroupEmail(),
    updateGeoEmailGroupData: updateGeoEmailGroupData(),
    applyFilters: applyFilters(),
    clearFilters: clearFilters()
  };
}

export const extraActions = createExtraActions();

export const geoEmailGroupActions = { ...actions, ...extraActions };
export const geoEmailGroupReducer = slice.reducer;
